import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { OBJECTIVES_EMPTY_SEARCH } from "@/router";
import { ArrowLeft, BookOpen, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StepIndicator } from "./step-indicator";
import { SetupStep, isSetupComplete } from "./setup-step";
import { ObjectiveStep } from "./objective-step";
import { KrStep } from "./kr-step";
import { useSaveWizard } from "./use-save-wizard";
import {
  WIZARD_STEPS,
  createInitialState,
  isObjectiveStepComplete,
  type WizardState,
} from "./wizard-state";
import { DEFAULT_YEAR } from "@/lib/year-options";

export function CoachWizardPage() {
  return (
    <ErrorBoundary fallback={<WizardErrorFallback />}>
      <CoachWizard />
    </ErrorBoundary>
  );
}

function WizardErrorFallback() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            The guided builder hit an unexpected error. Your draft has been discarded.
          </p>
          <Button onClick={() => navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH })}>Start Over</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function CoachWizard() {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(() => createInitialState(DEFAULT_YEAR));
  const [currentStep, setCurrentStep] = useState(0);
  const { save, isSaving } = useSaveWizard();
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerVisible, setFooterVisible] = useState(true);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry!.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isDirty =
    state.setup.level !== null ||
    state.setup.type !== null ||
    state.objective.statement.trim().length > 0 ||
    state.keyResults.some((k) => k.title.trim().length > 0);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleBackToObjectives = useCallback(() => {
    if (isDirty && !window.confirm("You have unsaved work. Leave the guided builder?")) {
      return;
    }
    navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH });
  }, [isDirty, navigate]);

  const handleSaveDraft = useCallback(async () => {
    const result = await save(state);
    if (result) {
      navigate({ to: "/objectives/$id", params: { id: result.objectiveId } });
    }
  }, [navigate, save, state]);

  const canAdvance =
    currentStep === 0
      ? isSetupComplete(state)
      : currentStep === 1
        ? isObjectiveStepComplete(state)
        : false;

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const continueLabel =
    currentStep === 1 ? "Continue to Key Results" : "Continue";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={handleBackToObjectives}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Objectives
        </Button>
      </div>

      <div>
        <h1 className="font-heading text-3xl font-semibold">Guided OKR Builder</h1>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
          The coach will walk you through writing a strong Objective and Key Results.
        </p>
      </div>

      <StepIndicator steps={WIZARD_STEPS} currentStep={currentStep} />

      <div key={currentStep} className="animate-in fade-in duration-300">
        {currentStep === 0 && <SetupStep state={state} onChange={setState} />}
        {currentStep === 1 && (
          <ObjectiveStep
            state={state}
            onChange={setState}
            onAdvance={() => setCurrentStep(2)}
          />
        )}
        {currentStep === 2 && (
          <KrStep
            state={state}
            onChange={setState}
            onBackToObjective={() => setCurrentStep(1)}
            onCommit={handleSaveDraft}
            isSaving={isSaving}
          />
        )}
      </div>

      {!footerVisible && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <div className="flex animate-bounce items-center gap-1.5 rounded-full bg-[hsl(var(--foreground))]/80 px-3 py-1.5 text-xs text-[hsl(var(--background))]">
            <ChevronDown className="h-3 w-3" />
            Scroll to continue
          </div>
        </div>
      )}

      <div ref={footerRef} className="flex items-center justify-between border-t pt-4">
          {currentStep > 0 ? (
            <Button
              variant="outline"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={isSaving}
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          {isLastStep ? (
            <Button
              onClick={handleSaveDraft}
              disabled={isSaving}
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            >
              <BookOpen className="mr-2 h-4 w-4" />
              {isSaving ? "Saving..." : "Save as Draft"}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentStep((s) => Math.min(WIZARD_STEPS.length - 1, s + 1))}
              disabled={!canAdvance}
            >
              {continueLabel}
            </Button>
          )}
      </div>
    </div>
  );
}
